import { FC } from "react";

import { Box, Stack } from "@mui/material";

import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Sparks as SparksIcon } from "@/icons/outlined";

interface PromptComposerProps {
  label: string;
  optionalLabel?: string;
  placeholder: string;
  submitLabel: string;
  value: string;
  minRows?: number;
  showSubmitIcon?: boolean;
  embedded?: boolean;
  isSubmitting: boolean;
  dataTest: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
}

export const PromptComposer: FC<PromptComposerProps> = ({
  label,
  optionalLabel,
  placeholder,
  submitLabel,
  value,
  minRows = 3,
  showSubmitIcon = false,
  embedded = false,
  isSubmitting,
  dataTest,
  onChange,
  onSubmit,
}) => {
  const canSubmit = Boolean(value.trim()) && !isSubmitting;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (canSubmit) {
        onSubmit();
      }
    }
  };

  const labelNode = (
    <DashboardTypography variant="body2" vibe="text-neutral-moderate">
      {label}
      {optionalLabel ? (
        <DashboardTypography component="span" variant="body2" vibe="text-neutral-quiet">
          {` ${optionalLabel}`}
        </DashboardTypography>
      ) : null}
    </DashboardTypography>
  );

  return (
    <Stack gap={0.5} width="100%">
      {!embedded && labelNode}
      <Stack
        gap={embedded ? 0.5 : 2}
        data-test={dataTest}
        sx={(theme) => ({
          width: "100%",
          padding: 2,
          backgroundColor: theme.palette.surface.neutral.white,
          ...(embedded
            ? {}
            : {
                borderRadius: 1,
                border: `1px solid ${theme.palette.border.neutral.quiet}`,
                "&:focus-within": {
                  borderColor: theme.palette.border.brand.intense,
                  boxShadow: `0 0 0 3px ${theme.palette.border.brand.moderate}`,
                },
              }),
        })}
      >
        {embedded && labelNode}
        <Box
          component="textarea"
          value={value}
          placeholder={placeholder}
          aria-label={label}
          rows={minRows}
          disabled={isSubmitting}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          sx={(theme) => ({
            width: "100%",
            border: "none",
            outline: "none",
            resize: "none",
            padding: 0,
            backgroundColor: "transparent",
            color: theme.palette.text.neutral.moderate,
            whiteSpace: "pre-wrap",
            overflowWrap: "break-word",
            ...theme.typography.body2,
            "&::placeholder": { color: theme.palette.text.neutral.supershy, opacity: 1 },
          })}
        />
        <Stack direction="row" alignItems="center" justifyContent="flex-end" marginTop={embedded ? 1 : 0}>
          <DashboardButton
            variant="filled"
            vibe="brand"
            size="small"
            disabled={!canSubmit}
            isLoading={isSubmitting}
            startIcon={showSubmitIcon ? <SparksIcon /> : undefined}
            data-test={`${dataTest}-submit`}
            onClick={onSubmit}
          >
            {submitLabel}
          </DashboardButton>
        </Stack>
      </Stack>
    </Stack>
  );
};
